import { StarsDropDown, Translation } from '@ps/web-shared-components';
import React, { useContext, useEffect, useMemo } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { Checkbox, PKR_LOADERS, Speed, useHomeGamesWidget } from '@swf/poker';

import CreateTournamentContext from '../CreateTournamentContext/CreateTournamentContext';
import useGetGameOptions from '../hooks/useGetGameOptions';
import type { FormValues } from '../utils/common';
import getOptionsByHandType from '../utils/getOptionsByHandType';
import getStandaloneOptions from '../utils/getStandaloneOptions';

import {
  TXTCLI_Breaks,
  TXTCLI_Format,
  TXTCLI_Late_Registration,
  TXTCLI_Payout,
  TXTCLI_Players_Per_Table,
  TXTCLI_Re_Entry,
  TXTCLI_Results_apply_to_Club_Standing_ELL,
  TXTCLI_Speed,
  TXTCLI_Starting_Chips,
  TXTCLI_mins
} from './AdvancedOptions.messages.json';
import styles from './AdvancedOptions.scss';

const AdvancedOptions: React.FC = () => {
  const {
    state: { widgetId }
  } = useContext(CreateTournamentContext);
  const { widgetSelect } = useHomeGamesWidget(widgetId);
  const loadingState = useSelector(widgetSelect.loading);
  const data = useSelector(widgetSelect.tournamentStructures);
  const loading = loadingState[PKR_LOADERS.tournamentStructures];

  const { change, getState } = useForm<FormValues>();
  const {
    values: { handType: handTypeValue }
  } = getState();
  const { handTypeOptions } = useGetGameOptions(data);

  const { tableSizeOptions } = useMemo(() => {
    const { handType, isHiLo } = JSON.parse((handTypeValue as string) ?? handTypeOptions[0].value);
    return getOptionsByHandType(data, handType, isHiLo);
  }, [data, handTypeOptions, handTypeValue]);

  const { startingChipsOptions } = useMemo(() => getStandaloneOptions(data), [data]);

  const {
    tournLevelLengthsOptions,
    tournFormatsOptions,
    tournLateRegLevelsOptions,
    payoutsOptions
  } = useMemo(() => getStandaloneOptions(data), [data]);

  useField('tableSize', { initialValue: tableSizeOptions[0]?.value });
  useField('startingChips', { initialValue: startingChipsOptions[0]?.value });
  const {
    input: { value: levelLength }
  } = useField('levelLength', {
    subscription: { value: true },
    initialValue: tournLevelLengthsOptions[0]?.value
  });
  const {
    input: { value: tournFormat }
  } = useField('tournFormat', { initialValue: tournFormatsOptions[0]?.value });
  useField('payout', { initialValue: payoutsOptions[0]?.value });
  const {
    input: { value: level }
  } = useField('tournLateRegLevel', {
    subscription: { value: true },
    initialValue: tournLateRegLevelsOptions[0]?.value
  });
  const {
    input: { value: tournReentry }
  } = useField('tournReentry', { defaultValue: false });
  const {
    input: { value: tournSyncBreaks }
  } = useField('tournSyncBreaks', { initialValue: true });
  const {
    input: { value }
  } = useField('countTowardsClubStandings', { defaultValue: true });

  const isRebuy = tournFormat.includes('REBUY');

  useEffect(() => {
    if (isRebuy && tournReentry) {
      change('tournReentry', 0);
    }
  }, [change, isRebuy, tournReentry]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    change(event.target.id, event.target.checked);
  };

  return (
    <div className={styles.advancedOptions}>
      <div>
        <StarsDropDown
          id="tableSize"
          label={TXTCLI_Players_Per_Table}
          options={tableSizeOptions}
          className={styles.dropdown}
          disabled={loading}
        />
      </div>
      <div>
        <StarsDropDown
          id="startingChips"
          label={TXTCLI_Starting_Chips}
          options={startingChipsOptions}
          className={styles.dropdown}
          disabled={loading}
        />
      </div>
      <div>
        <StarsDropDown
          id="levelLength"
          label={TXTCLI_Speed}
          options={tournLevelLengthsOptions}
          className={styles.dropdown}
          disabled={loading}
        />
      </div>
      <div>
        <StarsDropDown
          id="tournFormat"
          label={TXTCLI_Format}
          options={tournFormatsOptions}
          className={styles.dropdown}
          disabled={loading}
        />
      </div>
      <div className={styles.payout}>
        <StarsDropDown
          id="payout"
          label={TXTCLI_Payout}
          options={payoutsOptions}
          className={styles.dropdown}
          disabled={loading}
        />
      </div>
      <div className={styles.lateWrapper}>
        <div className={styles.dropdownWrapper}>
          <StarsDropDown
            id="tournLateRegLevel"
            label={TXTCLI_Late_Registration}
            options={tournLateRegLevelsOptions}
            className={styles.dropdown}
            disabled={loading}
          />
        </div>
        <div className={styles.lateRegistration}>
          <span>{`${level * Speed[levelLength] || 0}`}</span>
          <span>
            <Translation {...TXTCLI_mins} />
          </span>
        </div>
      </div>
      <div className={styles.checkboxes}>
        <div>
          <Checkbox
            id="tournReentry"
            value={tournReentry}
            checked={tournReentry}
            label={<Translation {...TXTCLI_Re_Entry} />}
            onChange={handleChange}
            disabled={isRebuy}
          />
        </div>
        <div>
          <Checkbox
            id="tournSyncBreaks"
            value={tournSyncBreaks}
            checked={tournSyncBreaks}
            label={<Translation {...TXTCLI_Breaks} />}
            onChange={handleChange}
          />
        </div>
        <div className={styles.applyResults}>
          <Checkbox
            id="countTowardsClubStandings"
            value={value}
            checked={value}
            label={<Translation {...TXTCLI_Results_apply_to_Club_Standing_ELL} />}
            onChange={handleChange}
          />
        </div>
      </div>
    </div>
  );
};

export default AdvancedOptions;
